declare global {
  interface Window {
    google: any;
  }
}

import { useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { loadGoogleMaps } from "@/lib/googleMapsLoader";

type MarkerData = {
  id: string | number;
  category: string;
  status: string;
  icon: LucideIcon;
  lat: number;
  lng: number;
  title?: string;
  description?: string;
  protocol?: string;
  address?: string;
  priority?: string;
};

type OccurrencesGoogleMapProps = {
  markers: MarkerData[];
  center?: [number, number];
  zoom?: number;
  statusConfig?: Record<string, { label: string }>;
};

const statusColors: Record<string, string> = {
  AWAITING_ANALYSIS: "#eab308",
  IN_PROGRESS: "#3b82f6",
  RESOLVED: "#22c55e",
  REJECTED: "#ef4444",
  CANCELLED: "#6b7280",
  REOPENED: "#f97316",
  RETURNED: "#a855f7",
  AWAITING_REANALYSIS: "#ca8a04",
  FILED: "#4b5563",
};

export const OccurrencesGoogleMap = ({
  markers,
  center = [-23.5505, -46.6333],
  zoom = 13,
  statusConfig,
}: OccurrencesGoogleMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // Carregar Google Maps API
  useEffect(() => {
    loadGoogleMaps(() => {
      setIsMapLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google || !isMapLoaded) return;

    // Validar coordenadas do centro
    const lat = center[0];
    const lng = center[1];

    if (!isFinite(lat) || !isFinite(lng)) {
      console.error("Coordenadas inválidas para o centro do mapa:", center);
      return;
    }

    // Criar o mapa
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat, lng },
      zoom: zoom,
      mapId: "DEMO_MAP_ID",
    });

    googleMapRef.current = map;

    return () => {
      googleMapRef.current = null;
    };
  }, [center, zoom, isMapLoaded]);

  useEffect(() => {
    if (!googleMapRef.current || !window.google || !isMapLoaded) return;

    try {
      // Limpar marcadores existentes
      markersRef.current.forEach((marker) => {
        marker.map = null;
      });
      markersRef.current = [];

      // Criar novos marcadores
      const newMarkers: any[] = [];
      const bounds = new window.google.maps.LatLngBounds();

      markers.forEach((markerData) => {
        // Validar coordenadas do marcador
        const lat = Number(markerData.lat);
        const lng = Number(markerData.lng);

        if (!isFinite(lat) || !isFinite(lng) || isNaN(lat) || isNaN(lng)) {
          console.warn("Marcador com coordenadas inválidas:", markerData);
          return;
        }

        const position = { lat, lng };
        const color = statusColors[markerData.status] || "#6b7280";

        // Criar elemento de pin customizado
        const pinElement = new window.google.maps.marker.PinElement({
          background: color,
          borderColor: adjustColorBrightness(color, -20),
          glyphColor: "#FFFFFF",
          scale: 1.2,
        });

        // Criar marcador avançado
        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: position,
          map: googleMapRef.current,
          content: pinElement.element,
          title: markerData.title || markerData.category,
        });

        // Criar InfoWindow
        const infoWindow = new window.google.maps.InfoWindow({
          content: createInfoWindowContent(markerData, statusConfig),
        });

        // Adicionar listener de click
        marker.addListener("click", () => {
          infoWindow.open({
            anchor: marker,
            map: googleMapRef.current,
          });
        });

        newMarkers.push(marker);
        bounds.extend(position);
      });

      markersRef.current = newMarkers;

      // Ajustar bounds se houver marcadores
      if (markers.length > 0 && markers.length > 1) {
        googleMapRef.current.fitBounds(bounds);
      } else if (markers.length === 1) {
        googleMapRef.current.setCenter({
          lat: markers[0].lat,
          lng: markers[0].lng,
        });
        googleMapRef.current.setZoom(15);
      }
    } catch (error) {
      console.error("Erro ao criar marcadores no mapa:", error);
    }
  }, [markers, statusConfig, isMapLoaded]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {!isMapLoaded && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <div className="text-sm text-muted-foreground">
            Carregando mapa...
          </div>
        </div>
      )}
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
};

// Função auxiliar para ajustar o brilho da cor
function adjustColorBrightness(color: string, percent: number): string {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
}

// Função para criar conteúdo do InfoWindow
function createInfoWindowContent(
  marker: MarkerData,
  statusConfig?: Record<string, { label: string }>,
): string {
  const statusLabel = statusConfig?.[marker.status]?.label || marker.status;
  const statusColor = statusColors[marker.status] || "#6b7280";

  return `
    <div style="padding: 8px; max-width: 300px;">
      <h3 style="font-weight: 600; font-size: 14px; margin-bottom: 4px; word-wrap: break-word;">
        ${marker.title || marker.category}
      </h3>
      ${
        marker.protocol
          ? `<p style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">
               Protocolo: ${marker.protocol}
             </p>`
          : ""
      }
      <p style="font-size: 12px; color: #6b7280; margin-bottom: 8px; word-wrap: break-word; white-space: normal;">
        ${marker.description || `Ocorrência de ${marker.category}`}
      </p>
      ${
        marker.address
          ? `<p style="font-size: 12px; color: #6b7280; margin-bottom: 8px; word-wrap: break-word;">
               📍 ${marker.address}
             </p>`
          : ""
      }
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${statusColor};"></span>
        <span style="font-size: 12px;">${statusLabel}</span>
      </div>
      ${
        marker.priority
          ? `<div style="margin-top: 8px;">
               <span style="font-size: 11px; padding: 2px 6px; background-color: ${getPriorityColor(marker.priority)}; color: white; border-radius: 4px;">
                 ${getPriorityLabel(marker.priority)}
               </span>
             </div>`
          : ""
      }
    </div>
  `;
}

// Função para obter cor da prioridade
function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    LOW: "#22c55e",
    MEDIUM: "#eab308",
    HIGH: "#f97316",
    URGENT: "#ef4444",
  };
  return colors[priority] || "#6b7280";
}

// Função para obter label da prioridade
function getPriorityLabel(priority: string): string {
  const labels: Record<string, string> = {
    LOW: "Baixa",
    MEDIUM: "Média",
    HIGH: "Alta",
    URGENT: "Urgente",
  };
  return labels[priority] || priority;
}
